import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, message, Space, Alert, Descriptions, Input, Tag, DatePicker } from 'antd';
import { CloudUploadOutlined, CloudDownloadOutlined, LinkOutlined, SyncOutlined, DollarOutlined, SaveOutlined, FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Paragraph } = Typography;

export default function GoogleSheetSync() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lastOutput, setLastOutput] = useState('');
  const [cms, setCms] = useState(null);
  const [cmsKey, setCmsKey] = useState('');
  const [cmsSaving, setCmsSaving] = useState(false);
  const [cmsImporting, setCmsImporting] = useState(false);
  const [cmsExporting, setCmsExporting] = useState(false);
  const [cmsMonth, setCmsMonth] = useState(dayjs().subtract(1, 'month'));

  const loadStatus = async () => {
    try { const res = await api.get('/gsheet/status'); setStatus(res.data); }
    catch (err) { console.error(err); }
    try { const r = await api.get('/gsheet/cms-revenue/status'); setCms(r.data); }
    catch (err) { console.error(err); }
  };
  useEffect(() => { loadStatus(); }, []);

  const saveCmsKey = async () => {
    setCmsSaving(true);
    try {
      await api.post('/gsheet/cms-revenue/save-key', { api_key: cmsKey });
      setCmsKey('');
      message.success('CMS API key saved');
      loadStatus();
    } catch (err) { message.error(err.response?.data?.error || 'Save failed'); }
    finally { setCmsSaving(false); }
  };

  const importCmsRevenue = async () => {
    setCmsImporting(true);
    try {
      const body = cmsMonth ? { months: [cmsMonth.format('YYYY-MM')] } : {};
      const res = await api.post('/gsheet/cms-revenue/import', body);
      const r = res.data?.result || {};
      setLastOutput(Object.entries(r).map(([k, v]) => `${k}: ${v}`).join('\n'));
      message.success('CMS revenue import done + synced to Sheet');
    } catch (err) { message.error(err.response?.data?.error || 'CMS import failed'); }
    finally { setCmsImporting(false); }
  };

  const importCmsLast6 = async () => {
    setCmsImporting(true);
    try {
      const res = await api.post('/gsheet/cms-revenue/import', {});
      const r = res.data?.result || {};
      setLastOutput(Object.entries(r).map(([k, v]) => `${k}: ${v}`).join('\n'));
      message.success('Last 6 months imported + synced to Sheet');
    } catch (err) { message.error(err.response?.data?.error || 'CMS import failed'); }
    finally { setCmsImporting(false); }
  };

  const downloadCmsExcel = async () => {
    if (!cmsMonth) { message.warning('Month select karo'); return; }
    setCmsExporting(true);
    try {
      const m = cmsMonth.format('YYYY-MM');
      const res = await api.get('/gsheet/cms-revenue/export', { params: { month: m }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `cms_revenue_${m}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      message.success('Excel (CSV) download ho gaya');
    } catch (err) {
      let msg = 'Download failed';
      try { msg = JSON.parse(await err.response?.data?.text())?.error || msg; } catch (e) {}
      message.error(msg);
    }
    finally { setCmsExporting(false); }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/gsheet/sync');
      setLastOutput(res.data?.output || '');
      message.success('Dashboard → Sheet sync done');
    } catch (err) { message.error(err.response?.data?.error || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const res = await api.post('/gsheet/import');
      const r = res.data?.result || {};
      setLastOutput(Object.entries(r).map(([k, v]) => `${k}: ${v}`).join('\n'));
      message.success('Sheet → Dashboard import done');
    } catch (err) { message.error(err.response?.data?.error || 'Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 16 }}
        message="Two-way Google Sheet sync"
        description={
          <div>
            <Paragraph style={{ marginBottom: 4 }}>
              <b>Sync to Sheet</b>: dashboard ka latest data sheet me daalta hai (roz raat 8:00 PM auto).
            </Paragraph>
            <Paragraph style={{ marginBottom: 4 }}>
              <b>Import from Sheet</b>: sheet me kiye gaye changes wapas dashboard me laata hai —
              Attendance (P/A/H), Song Metadata (ISRC key), aur Channels &amp; Revenue (USD).
            </Paragraph>
            <Paragraph type="warning" style={{ marginBottom: 0 }}>
              Heading (column ke naam) mat badalna — sirf values edit karo, warna match toot jayega.
            </Paragraph>
          </div>
        }
      />

      {status && (
        <Card size="small" style={{ marginBottom: 16, borderRadius: 12 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Configured">{status.configured ? 'Yes' : 'No'}</Descriptions.Item>
            {status.link && (
              <Descriptions.Item label="Spreadsheet">
                <a href={status.link} target="_blank" rel="noreferrer"><LinkOutlined /> Open Google Sheet</a>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      <Space wrap>
        <Button type="primary" icon={<CloudUploadOutlined />} loading={syncing} onClick={runSync} disabled={!status?.configured}>
          Sync to Sheet now
        </Button>
        <Button icon={<CloudDownloadOutlined />} loading={importing} onClick={runImport} disabled={!status?.configured}>
          Import from Sheet now
        </Button>
        <Button icon={<SyncOutlined />} onClick={loadStatus}>Refresh</Button>
      </Space>

      <Card
        size="small"
        title={<span><DollarOutlined /> Auto YouTube Revenue (cms.intubemedia.com)</span>}
        style={{ marginTop: 16, borderRadius: 12 }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Har mahine ki <b>5 tareekh</b> ko pichhle poore mahine ka revenue (USD) CMS se apne-aap
          dashboard + Sheet me aa jayega. Yahan se koi bhi month select karke turant import ya
          Excel download kar sakte ho. Naye channels CMS me add karoge to import par apne-aap ban jayenge.
        </Paragraph>
        <Descriptions column={1} size="small" style={{ marginBottom: 8 }}>
          <Descriptions.Item label="API Key">
            {cms?.configured ? <Tag color="green">{cms.key_masked || 'Saved'}</Tag> : <Tag color="red">Not set</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Endpoint">{cms?.url || '-'}</Descriptions.Item>
          <Descriptions.Item label="Currency">
            <Typography.Text type="secondary">Revenue USD me aata hai (CMS se).</Typography.Text>
          </Descriptions.Item>
        </Descriptions>
        <Space wrap style={{ marginBottom: 12 }}>
          <span>Month:</span>
          <DatePicker
            picker="month"
            value={cmsMonth}
            onChange={(v) => setCmsMonth(v)}
            disabledDate={(d) => d && d.isAfter(dayjs().subtract(1, 'month').endOf('month'))}
            allowClear={false}
          />
          <Button type="primary" icon={<DollarOutlined />} loading={cmsImporting} onClick={importCmsRevenue} disabled={!cms?.configured}>
            Import This Month Now
          </Button>
          <Button icon={<FileExcelOutlined />} loading={cmsExporting} onClick={downloadCmsExcel} disabled={!cms?.configured}>
            Download Excel
          </Button>
          <Button icon={<DollarOutlined />} loading={cmsImporting} onClick={importCmsLast6} disabled={!cms?.configured}>
            Import Last 6 Months
          </Button>
        </Space>
        <Space wrap>
          <Input.Password
            placeholder="Naya CMS API key paste karo (khali chhodo to same rahega)"
            value={cmsKey}
            onChange={(e) => setCmsKey(e.target.value)}
            autoComplete="new-password"
            style={{ width: 320 }}
          />
          <Button icon={<SaveOutlined />} loading={cmsSaving} onClick={saveCmsKey} disabled={!cmsKey}>
            Save Key
          </Button>
        </Space>
      </Card>

      {lastOutput && (
        <Card size="small" title="Last run" style={{ marginTop: 16, borderRadius: 12 }}>
          <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>{lastOutput}</pre>
        </Card>
      )}
    </div>
  );
}
